import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { SlidersHorizontal } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import './room.css';
import { aaiApi } from '../../services/aaiApi';
import { usePermissions } from '../../hooks/usePermissions';
import type { OrbitAsk, OrbitJourney, OrbitOpportunityItem, OrbitOverview, OrbitOverviewDossier, OrbitRoomFact, OrbitRoomPurpose, OrbitRoomSummary } from '../../types/aai';
import { CompanyTabPanel } from '../../components/company/CompanyTabs';
import ConnectorsCatalog from '../../components/onboarding/ConnectorsCatalog';
import { CountUp, ErrorNote, Loading, Panel, PanelHead } from '../../components/aa';
import { useOrbitRoom, usePeople } from './useOrbitRoom';
import IntakeJourneyPanel from './IntakeJourneyPanel';
import { JourneyContext } from './JourneyContext';
import { MapFill } from './MapFill';
import { CoverageRadar, FactsDonut, MappedRing, cardsOf } from './OverviewCharts';
import { PhaseDepartments } from './PhaseDepartments';
import { PhaseTools } from './PhaseTools';
import { PhasePersonIntake } from './PhasePersonIntake';
import { MyPart } from './MyPart';
import { ClarityReport } from './ClarityReport';
import { RunView } from './RunView';
import { completion, completionWord, phaseOfScreen, phaseRows, screenOfPhase, shownPhases } from './phases';
import type { PhaseRow } from './phases';
import { WORDS, bucketOfDossier, handedLine, originLine, plural, responsibleLine, scopeName, shortName, statusChip, whenLabel } from './roomCopy';
import {
  AddContextDrawer, AddDepartmentDrawer, AddPersonDrawer, AddQuestionDrawer, AskDrawer, ImportRosterDrawer,
} from './RoomDrawers';
import type { AskMode } from './RoomDrawers';
import PeopleArea from './sessions/PeopleArea';
import type { PeopleTab } from './sessions/PeopleArea';

/*
 * The company page, in the shape of the 15 Sep artifact ("Orbit, the
 * framework"). Three tabs: Overview (where the company is, at a glance),
 * Journey (the phases, one open at a time) and Opportunities (locked until
 * the maps are true). The maps themselves are reached through the phases, not
 * listed on their own: a person who opens this page is either checking where
 * they are or doing the open phase.
 *
 * The same body serves the owner looking at their own company and the partner
 * looking at a client. What changes is the viewer, which sets the scope
 * server-side, and the grants, which decide which buttons render. Every noun
 * comes from roomCopy.WORDS.
 */

export type RoomViewer = 'partner' | 'owner' | 'department' | 'champion' | 'employee';

type MainTab = 'overview' | 'journey' | 'opps' | 'people';
type Fill = 'collected' | 'tocollect' | 'opps' | 'team' | 'departments' | 'connections';
type OppFilter = 'all' | 'company' | 'person';

type InlineMode = 'answer' | 'hand' | 'correct';

interface InlineState {
  /** The ask id, or the field key of the fact being corrected. */
  id: string;
  mode: InlineMode;
  value: unknown;
  to: string;
  note: string;
}

type DrawerState =
  | { kind: 'ask'; ask: OrbitAsk; mode: AskMode }
  | { kind: 'addq' }
  | { kind: 'context' }
  | { kind: 'adddept' }
  | { kind: 'addperson' }
  | { kind: 'import' }
  | null;

const FORMAT: Record<string, string> = { say: 'voice or text', choose: 'one tap', choose_many: 'pick several', number: 'a number', people: 'pick people' };

/* The short key beside a row, the artifact's IDENTITY / GOAL / HEAD. */
const keyOf = (label: string): string => {
  const words = label.replace(/[?.]$/, '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'CARD';
  if (words[0].length <= 3 && words.length > 1) return `${words[0]} ${words[1]}`.toUpperCase();
  return words[0].toUpperCase();
};
const askKeyOf = (ask: OrbitAsk): string => {
  if (ask.field_key) return ask.field_key.split('.').slice(-1)[0].replace(/_/g, ' ').toUpperCase().slice(0, 18);
  if (ask.scope.startsWith('tool:')) return ask.scope.slice(5).replace(/_/g, ' ').toUpperCase().slice(0, 18);
  return keyOf(ask.question);
};

const useOverview = (clientId: string, tick: number) => {
  const [overview, setOverview] = useState<OrbitOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!clientId) return;
    let live = true;
    aaiApi.getOrbitOverview()
      .then((res) => { if (live) setOverview(res.data); })
      .catch(() => { if (live) setError('Could not load the Room.'); });
    return () => { live = false; };
  }, [clientId, tick]);
  return { overview, error };
};

/*
 * The company, for somebody whose own lens is one department.
 *
 * A head reads their level and the company as a COUNT. Counts are neither
 * another department's facts nor any individual's answers, which is what makes
 * this the one company-wide thing this screen may carry, and why the line below
 * says so rather than leaving the reader to wonder what else is behind it.
 *
 * The server decides who gets this: `company_summary` is null for a viewer whose
 * lens already reads the company, so there is nothing to draw and no second rule
 * here to disagree with it.
 */
const CompanyCount: React.FC<{ summary: OrbitRoomSummary }> = ({ summary }) => {
  const parts = [
    `${summary.known} ${plural(summary.known, 'fact', 'facts')} confirmed by a person`,
    `${summary.confirm} read and waiting to be confirmed`,
    `${summary.open} ${plural(summary.open, 'question', 'questions')} still open`,
  ];
  if (summary.waiting > 0) parts.push(`${summary.waiting} of them already with somebody`);
  return (
    <div className="det-sum panel tint">
      <p className="kick" style={{ color: 'var(--ink)' }}>The whole company, as a count</p>
      <p className="nl">Across every department: {parts.join(', ')}.</p>
      <div className="srcline">
        Counts only. No other department&apos;s facts and no individual&apos;s answers are readable from here.
      </div>
    </div>
  );
};

export const OrbitRoomPanel: React.FC<{
  viewer: RoomViewer;
  clientId: string;
  /** Opens the Room on one dossier. */
  initialScope?: string;
  /** 'panel' sits inside another page; 'page' is the Room as the page itself, the artifact's shape. */
  layout?: 'panel' | 'page';
  /** The company the eyebrow names in page layout. */
  clientName?: string;
  /** A fixed page title; by default the open tab names the page. */
  pageTitle?: string;
  /** More links beside Adjust framework in the page head. */
  extraActions?: React.ReactNode;
  /** The page around it already shows the Overview, so the Room keeps only its other tabs. */
  hideOverview?: boolean;
  /** Called when the reader comes back from one of the Room's screens, so the page around it can re-read. */
  onChanged?: () => void;
}> = ({ viewer, clientId, initialScope = '', layout = 'panel', clientName = '', pageTitle, extraActions, hideOverview = false, onChanged }) => {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const me = (localStorage.getItem('user_email') || '').toLowerCase();
  const isPartner = viewer === 'partner';
  /*
   * A department head runs one department, and this screen is that department.
   *
   * The Room is written for somebody reading the whole company: three tabs over
   * the company's understanding, its six phases and its opportunities. None of
   * that is a head's to read. Their dossier list is already just their own
   * department (the server scopes it), so the panel opens straight into it and
   * the company chrome never renders.
   */
  const isDept = viewer === 'department';

  const [tab, setTab] = useState<MainTab>('overview');
  const lastTab = useRef<MainTab>('overview');
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  useEffect(() => {
    if (lastTab.current !== 'overview' && tab === 'overview' && changedRef.current) changedRef.current();
    lastTab.current = tab;
  }, [tab]);
  /* A page that hides the Overview opens the Room's screens from its own cards. */
  useEffect(() => {
    if (!hideOverview) return undefined;
    const onOpen = (e: Event) => {
      const next = (e as CustomEvent<{ tab: MainTab }>).detail?.tab;
      if (next) setTab(next);
    };
    window.addEventListener('orbit-room:tab', onOpen);
    return () => window.removeEventListener('orbit-room:tab', onOpen);
  }, [hideOverview]);
  const [searchParams] = useSearchParams();
  const companionTarget = searchParams.get('companion');
  useEffect(() => {
    if (companionTarget !== 'part' || isPartner || isDept) return;
    setTab('journey');
  }, [companionTarget, searchParams, isPartner, isDept]);
  /* Links into the partner's People area: ?tab=people&person=<email> opens a
     person, ?tab=sessions&session=<id> (an "answered" notice) the responses. */
  const askedTab = searchParams.get('tab');
  const askedPerson = searchParams.get('person') || '';
  const askedSession = searchParams.get('session') || '';
  let peopleTab: PeopleTab = 'people';
  if (askedTab === 'sessions') peopleTab = 'sessions';
  useEffect(() => {
    if (!isPartner || (askedTab !== 'people' && askedTab !== 'sessions')) return;
    setTab('people');
  }, [askedTab, askedPerson, askedSession, isPartner]);
  /* Everyone at the company answers their own intakes on Your part, with or
     without a Solution Partner: the Owner Business, Tool and Person, a head
     their department and Person, everybody else Person. Only the partner,
     who runs the engagement, sees the Journey. */
  const [deptPart, setDeptPart] = useState(true);
  const ownPart = !isPartner;
  // 0 means no phase is open and the tabs are showing.
  const [phase, setPhase] = useState(0);
  const [journey, setJourney] = useState<OrbitJourney | null>(null);
  const [phaseBusy, setPhaseBusy] = useState(false);
  const [openKey, setOpenKey] = useState<string>(initialScope);
  const [fill, setFill] = useState<Fill>('collected');
  const [oppFilter, setOppFilter] = useState<OppFilter>('all');
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [tick, setTick] = useState(0);
  const [declining, setDeclining] = useState<{ id: string; note: string } | null>(null);
  const [oppBusy, setOppBusy] = useState(false);
  const [selFact, setSelFact] = useState(-1);
  const [openAsk, setOpenAsk] = useState('');
  // Answering, handing on and correcting all happen in the open row. The
  // drawer stays for the long read: the chain, the history, later and remove.
  const [inline, setInline] = useState<InlineState | null>(null);
  const [inlineBusy, setInlineBusy] = useState(false);

  const { overview, error: overviewError } = useOverview(clientId, tick);
  useEffect(() => {
    let live = true;
    aaiApi.getOrbitPhases().then((res) => { if (live) setJourney(res.data); }).catch(() => undefined);
    return () => { live = false; };
  }, [clientId, tick]);
  const dossiers = useMemo(() => (overview ? overview.dossiers : []), [overview]);
  const active: OrbitOverviewDossier | null = useMemo(() => {
    if (isDept) return dossiers.find((d) => d.kind === 'department') || null;
    if (!openKey) return null;
    return dossiers.find((d) => d.key === openKey) || null;
  }, [dossiers, openKey, isDept]);

  let purpose: OrbitRoomPurpose = 'business';
  let scope = '';
  if (active && active.kind === 'department') { purpose = 'department'; scope = active.key; }
  if (active && active.kind === 'person') purpose = 'person';
  const { view, loading, error, reload: reloadView } = useOrbitRoom(clientId, purpose, scope);
  const people = usePeople(Boolean(view));
  const reload = () => { reloadView(); setTick((t) => t + 1); };

  const canAdd = can('aai_room_ask_add');
  const canConfirm = can('aai_room_fact_confirm');
  const canDelegate = can('aai_room_delegate') && viewer !== 'employee';
  const canContext = can('aai_room_context_add');
  const canPeople = can('aai_room_people_manage') && viewer !== 'employee';
  /*
   * Adding a department is not the same right as adding a person to one. The
   * shape of the org is the Owner's (and the partner preparing on their behalf);
   * a department head runs one department and does not get to invent another.
   */
  const canDepartments = can('aai_manage_departments');
  const canAnswerOwn = can('aai_room_answer');
  const canAnswerFor = can('aai_room_answer_for');
  const canPromote = can('aai_manage_framework');
  const canDecide = can('aai_approve_requests') && !isPartner;
  const canDiscover = can('aai_manage_program');
  const canPhase = can('aai_room_phase_mark');
  const storedClientName = clientName || localStorage.getItem('client_name') || '';

  const canAnswer = (ask: OrbitAsk): boolean => {
    if (isPartner) return canAnswerFor;
    if (!canAnswerOwn) return false;
    if (ask.responsible_email === me) return true;
    if (view && view.viewer.full) return true;
    if (view && (view.viewer.role === 'DEPARTMENT_HEAD' || view.viewer.role === 'TEAM_LEAD')) return ask.scope === view.viewer.department;
    return false;
  };
  const canEdit = (ask: OrbitAsk): boolean => {
    if (!canAdd) return false;
    if (ask.created_by.toLowerCase() === me) return true;
    return Boolean(view && view.viewer.full);
  };

  const decideProposal = async (d: OrbitOverviewDossier, accept: boolean) => {
    if (!d.proposal) return;
    try {
      if (accept) {
        await aaiApi.decideOrbitProposals({ accept: [d.proposal.id], dismiss: [] });
        toast.success(`${d.proposal.name} is now a department.`);
      } else {
        await aaiApi.decideOrbitProposals({ accept: [], dismiss: [d.proposal.id] });
      }
      reload();
    } catch {
      toast.error('Could not decide on that department.');
    }
  };

  const open = (key: string, where: Fill = 'collected') => {
    setOpenKey(key);
    setFill(where);
    setSelFact(-1);
    setOpenAsk('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const back = () => setOpenKey('');
  const toCompany = () => {
    setOpenKey('');
    setPhase(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const openPhase = (row: PhaseRow) => {
    setPhase(row.n);
    setOpenKey('');
    aaiApi.startOrbitPhase(row.n).catch(() => undefined);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const markPhase = async (row: PhaseRow, state: 'done' | 'open') => {
    setPhaseBusy(true);
    try {
      const res = await aaiApi.markOrbitPhase(row.n, state);
      setJourney(res.data);
      if (state === 'done') toast.success(`Phase ${row.n} closed. The next one is open.`);
      if (state === 'open') toast.success(`Phase ${row.n} is open again.`);
    } catch {
      toast.error('Could not change that phase.');
    } finally {
      setPhaseBusy(false);
    }
  };

  const promote = async (ask: OrbitAsk) => {
    let target: 'framework' | 'client' = 'client';
    if (isPartner) target = 'framework';
    try {
      const res = await aaiApi.promoteOrbitAsk(ask.id, target);
      let where = 'this client\'s dossier';
      if (target === 'framework') where = 'your framework';
      toast.success(`Added to ${where} as a standard question for this industry (${res.data.key}).`);
      reload();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || 'Could not promote the question.');
    }
  };

  const targetsFor = (ask: OrbitAsk) => people.filter((p) => p.email !== me && !ask.chain.some((c) => c.from === p.email));
  const suggestedFor = (ask: OrbitAsk): string => {
    const targets = targetsFor(ask);
    const owner = targets.find((p) => p.role === 'OWNER');
    if (isPartner && owner) return owner.email;
    const head = targets.find((p) => ask.scope && p.department === ask.scope && p.role === 'DEPARTMENT_HEAD');
    if (head) return head.email;
    if (owner) return owner.email;
    return '';
  };
  const roleWord = (role: string): string => role.toLowerCase().split('_').join(' ');
  const firstValue = (ask: OrbitAsk): unknown => {
    if (ask.kind === 'choose_many') return [];
    return '';
  };

  const inlineAnswerControl = (ask: OrbitAsk): React.ReactNode => {
    const value = inline?.value;
    if (ask.kind === 'choose') {
      return (
        <div className="in-chips">
          {ask.options.map((o) => (
            <button key={o.value} type="button" className={clsx(value === o.value && 'on')} onClick={() => patchInline({ value: o.value })}>{o.label}</button>
          ))}
        </div>
      );
    }
    if (ask.kind === 'choose_many') {
      const picked: string[] = Array.isArray(value) ? (value as string[]) : [];
      const toggle = (v: string) => {
        if (picked.includes(v)) {
          patchInline({ value: picked.filter((x) => x !== v) });
          return;
        }
        patchInline({ value: [...picked, v] });
      };
      return (
        <div className="in-chips">
          {ask.options.map((o) => (
            <button key={o.value} type="button" className={clsx(picked.includes(o.value) && 'on')} onClick={() => toggle(o.value)}>{o.label}</button>
          ))}
        </div>
      );
    }
    if (ask.kind === 'number') {
      return (
        <label>
          <b>Answer</b>
          <input type="number" autoFocus value={String(value ?? '')} onChange={(e) => patchInline({ value: e.target.value })} placeholder="A number" />
        </label>
      );
    }
    let placeholder = 'Plain words are enough. Orbit turns them into things to verify.';
    if (ask.kind === 'people') placeholder = 'A name or an email. Several, separated by commas.';
    return (
      <label>
        <b>Answer</b>
        <textarea rows={2} autoFocus value={String(value ?? '')} onChange={(e) => patchInline({ value: e.target.value })} placeholder={placeholder} />
      </label>
    );
  };

  const startInline = (id: string, mode: InlineMode, value: unknown, to = '') => {
    if (inline && inline.id === id && inline.mode === mode) {
      setInline(null);
      return;
    }
    setInline({ id, mode, value, to, note: '' });
  };
  const patchInline = (patch: Partial<InlineState>) => setInline((prev: InlineState | null) => {
    if (!prev) return prev;
    return { ...prev, ...patch };
  });
  const emptyAnswer = (value: unknown): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    return false;
  };

  const saveInlineAnswer = async (ask: OrbitAsk) => {
    if (!inline) return;
    if (emptyAnswer(inline.value)) {
      toast.info('Choose or write an answer first.');
      return;
    }
    setInlineBusy(true);
    try {
      await aaiApi.answerOrbitAsk(ask.id, inline.value);
      let word = 'Answered. It moved to Collected with you as the source.';
      if (isPartner) word = 'Saved. The owner sees it as to confirm, with you as the source.';
      toast.success(word);
      setInline(null);
      reload();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || 'Could not save the answer.');
    } finally {
      setInlineBusy(false);
    }
  };

  const saveInlineHand = async (ask: OrbitAsk) => {
    if (!inline) return;
    if (!inline.to) {
      toast.info('Pick a person first.');
      return;
    }
    setInlineBusy(true);
    try {
      await aaiApi.delegateOrbitAsk(ask.id, { to_email: inline.to, note: inline.note });
      const target = people.find((p) => p.email === inline.to);
      let word = `Handed to ${shortName(inline.to, people)}. No email sent; Orbit asks it in their onboarding.`;
      if (target && target.onboarded) word = `Handed to ${shortName(inline.to, people)}. They see it on their Home.`;
      toast.success(word);
      setInline(null);
      reload();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || 'Could not hand this on.');
    } finally {
      setInlineBusy(false);
    }
  };

  const saveInlineCorrection = async (f: OrbitRoomFact) => {
    if (!inline || !view) return;
    const value = String(inline.value || '').trim();
    if (!value) {
      toast.info('Write the corrected value first.');
      return;
    }
    setInlineBusy(true);
    try {
      await aaiApi.correctOrbitFact({ field_key: f.field_key, scope: view.scope, value });
      toast.success(`Corrected. ${f.label} updated and marked known.`);
      setInline(null);
      reload();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || 'Could not save the correction.');
    } finally {
      setInlineBusy(false);
    }
  };

  const confirmFact = async (f: OrbitRoomFact) => {
    if (!view) return;
    try {
      await aaiApi.confirmOrbitFact({ field_key: f.field_key, scope: view.scope });
      toast.success(`Confirmed. ${f.label} now reads as known.`);
      reload();
    } catch {
      toast.error('Could not confirm.');
    }
  };

  const decide = async (item: OrbitOpportunityItem, decision: 'approved' | 'declined', comment?: string) => {
    if (!item.request_id) return;
    setOppBusy(true);
    try {
      await aaiApi.decideRequest(item.request_id, { decision, comment });
      let word = 'Confirmed. It becomes a project.';
      if (decision === 'declined') word = 'Declined, with your note on the record.';
      toast.success(word);
      setDeclining(null);
      reload();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || 'Could not record the decision.');
    } finally {
      setOppBusy(false);
    }
  };
  const sendToClient = async (item: OrbitOpportunityItem) => {
    setOppBusy(true);
    try {
      await aaiApi.sendOpportunity(item.id);
      toast.success('Sent to the owner for a decision.');
      reload();
    } catch {
      toast.error('Could not send it.');
    } finally {
      setOppBusy(false);
    }
  };
  const discover = async () => {
    setOppBusy(true);
    try {
      const res = await aaiApi.discoverOpportunities();
      toast.success(`${plural(res.data.created, 'opportunity', 'opportunities')} found from the file.`);
      reload();
    } catch {
      toast.error('Could not look for opportunities.');
    } finally {
      setOppBusy(false);
    }
  };

  if (!overview && !overviewError) return <Panel><Loading label={`Loading the ${WORDS.room}`} /></Panel>;
  if (overviewError || !overview) return <Panel><div className="px-6 py-5"><ErrorNote message={overviewError || `Could not load the ${WORDS.room}.`} /></div></Panel>;

  /* ── what belongs to the open dossier ─────────────────────────────── */
  const bucketKeys = (() => {
    if (!active) return [] as string[];
    if (active.kind === 'department') return ['dept', 'people'];
    if (active.kind === 'person') return ['me'];
    return [bucketOfDossier(active.kind, active.bucket)];
  })();
  const buckets = (view ? view.buckets : []).filter((b) => bucketKeys.includes(b.key));
  const facts: OrbitRoomFact[] = buckets.flatMap((b) => b.facts);
  const asks: OrbitAsk[] = buckets.flatMap((b) => b.asks);
  const dossierSources = Array.from(new Set(facts.map((f) => f.source).filter(Boolean)));

  const oppItems = overview.opportunities.items || [];
  const oppsFor = (filter: OppFilter, dossier?: OrbitOverviewDossier | null): OrbitOpportunityItem[] => {
    if (dossier) {
      if (dossier.kind === 'company') return oppItems.filter((i) => i.group === 'company');
      if (dossier.kind === 'department') return oppItems.filter((i) => i.department === dossier.key);
      if (dossier.kind === 'people') return oppItems.filter((i) => i.group === 'person');
      return [];
    }
    if (filter === 'all') return oppItems;
    return oppItems.filter((i) => i.group === filter);
  };

  const srcline = (items: string[]): string => {
    if (items.length === 0) return 'FROM · nothing read yet';
    return `FROM · ${items.join(' · ')} · nothing assumed, everything traceable`;
  };

  /* ── the page head ─────────────────────────────────────────────────── */
  const TABS: { id: MainTab; label: string; sub: string }[] = [
    { id: 'overview', label: WORDS.overview, sub: 'Where you are, at a glance' },
    ownPart
      ? { id: 'journey', label: 'Your part', sub: 'Your cards, and your work' }
      : { id: 'journey', label: WORDS.journey, sub: 'The intake, section by section' },
    { id: 'opps', label: WORDS.opportunities, sub: 'What Orbit finds for you' },
  ];
  if (isPartner) TABS.push({ id: 'people', label: 'People', sub: 'Who is in, sessions and answers' });
  const shownTabs = TABS.filter((t) => !(hideOverview && t.id === 'overview'));
  let pageName = isPartner ? 'The company' : 'Your company';
  if (pageTitle) pageName = pageTitle;
  let eyebrow = 'Orbit';
  if (storedClientName) eyebrow = `${storedClientName} · Orbit`;
  // Which questions Orbit opens with belongs to the partner and the platform,
  // never to the company being asked, so this shows for a Solution Partner.
  // A provider's clients run the Agents Anywhere framework, which nobody edits here.
  const isSolutionPartner = localStorage.getItem('user_role') === 'consultant';
  let frameworkPath = '';
  if (isSolutionPartner) frameworkPath = '/partner/framework';
  const toFramework = () => navigate(`${frameworkPath}?client_id=${clientId}&client_name=${encodeURIComponent(storedClientName)}&back=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  const headActions = (
    <div className="flex items-center gap-2.5">
      {extraActions}
      {isPartner && frameworkPath && canPromote && (
        <button
          type="button"
          className="group flex items-center gap-2.5 whitespace-nowrap rounded-act border border-line-strong bg-white px-3.5 py-2 text-left transition-colors hover:border-ink"
          title="The questions this client is asked and the dossier they fill, for this client alone"
          onClick={toFramework}
        >
          <SlidersHorizontal size={15} aria-hidden className="shrink-0 text-ink-dim transition-colors group-hover:text-ink" />
          <span className="flex flex-col gap-px">
            <strong className="text-[12.5px] font-semibold leading-[1.3] text-ink">Framework</strong>
            <small className="text-[10.5px] leading-[1.3] text-ink-dim">This client&rsquo;s questions and dossier</small>
          </span>
        </button>
      )}
    </div>
  );

  let roomTitle = WORDS.room;
  let roomDesc: string | undefined;
  if (hideOverview) roomDesc = 'Answer the cards that are yours, and review what Orbit finds.';
  if (isDept) {
    roomTitle = 'Department context';
    roomDesc = 'What Orbit knows about your department, and what it still needs from you.';
  }

  const tabs = (
    <nav className="mtabs">
      {shownTabs.map((t) => (
        <button key={t.id} type="button" className={clsx('mtab', tab === t.id && 'on')} onClick={() => setTab(t.id)}>
          <strong>{t.label}</strong><small>{t.sub}</small>
        </button>
      ))}
    </nav>
  );

  /* ── Overview ─────────────────────────────────────────────────────── */
  const deptName = isDept ? overview.department_name || '' : '';
  /* The owner and the head each have a People tab below this panel. */
  const peopleLinked = viewer === 'owner' || isDept;
  const openPeople = () => {
    navigate(`${window.location.pathname}?tab=people`, { replace: true });
    window.setTimeout(() => document.getElementById('context-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const understanding = (() => {
    let sentence = 'This is your company, as Orbit reads it.';
    let narrative = 'Give Orbit a website or a document and this line fills in with what it read.';
    if (overview.sentence) narrative = overview.sentence;
    if (viewer === 'partner') sentence = 'This is the client, as Orbit reads it.';
    if (deptName) sentence = `This is ${deptName}, as Orbit reads it.`;
    /* The counts and percentages live in the charts beside this; the paragraph
       only names where Orbit is still short. */
    const thin = overview.thin || [];
    let tally = '';
    if (thin.length > 0 && viewer !== 'partner') tally = `Orbit still needs more on ${thin.map((a) => a.label).join(', ')}.`;
    if (thin.length > 0 && thin.length === overview.areas.length && viewer !== 'partner') tally = 'Every area still needs answers before Orbit can rely on it.';
    let handoff = '';
    if (viewer === 'partner') handoff = 'Orbit prepared. Ready for handoff to the owner.';
    if (viewer === 'partner' && thin.length > 0) handoff = `Not ready for handoff yet. Orbit still needs more on ${thin.map((a) => a.label).join(', ')}.`;
    return (
      <div className="panel tint ov-sum">
        <p className="kick" style={{ color: 'var(--ink)' }}>Current understanding</p>
        <h2>{sentence}</h2>
        <p className="nl">{narrative} {tally}</p>
        {handoff && <p className="nl"><strong>{handoff}</strong></p>}
        {/* The source line is hidden for owners for now. */}
        {viewer !== 'owner' && <div className="srcline">{srcline(overview.sources)}</div>}
      </div>
    );
  })();

  const rows = phaseRows(journey, overview);
  const doneMax = journey ? journey.done_max : 0;
  // Parked with the "Now · Phase" card below.
  // const nowRow = rows.find((r) => r.state === 'now') || rows[rows.length - 1];
  const pct = completion(overview);
  const thinCount = (overview.thin || []).length;
  let mappedLine = 'Every area has enough to work with';
  if (thinCount > 0) mappedLine = `${thinCount} of ${overview.areas.length} areas still thin`;
  const cards = cardsOf(overview, Boolean(deptName));

  /* Dealing the frame's last card IS the decision "Mark done" asks for, so the
     phase closes on it. That button exists for the partner who has heard enough
     while cards are still open, not for the person who just answered every one
     of them and is being asked to say so twice. The completion card promises
     phase 2 is ready; this is what makes that true. Reopening still works: a
     map with no cards left to deal never closes itself again. It closes only
     when the frame is the phase that is open now: a partner can put it later
     in the order, and closing it early would close the steps before it too. */
  const frameN = phaseOfScreen(journey, 'frame');
  const closeFrame = async () => {
    if (!canPhase || !frameN || frameN !== doneMax + 1) return;
    try {
      const res = await aaiApi.markOrbitPhase(frameN, 'done');
      setJourney(res.data);
    } catch {
      // The phase keeps its Mark done button; nothing is lost.
    }
  };

  const overviewPane = (
    <>
      {/* Parked: the "Now · Phase N" card (the open journey phase, e.g. The
          clarity report, with its action button). Not used for now.
      {nowRow && (
        <div className="ov-now">
          <div className="n">{nowRow.n}</div>
          <div className="txt">
            <p className="kick">Now · Phase {nowRow.n}</p>
            <strong>{nowRow.title}</strong>
            <p>{nowRow.purpose}</p>
          </div>
          <button type="button" onClick={() => openPhase(nowRow)}>{nowRow.action}</button>
        </div>
      )}
      */}
      <div className="ov-grid2">
        {understanding}
        <div className="ov-panel">
          <h3>Mapped so far</h3>
          <small>Average of the {overview.areas.length} areas below</small>
          <div className="ov-ring">
            <MappedRing pct={pct} />
            <div className="lbl">
              <strong>{completionWord(pct, (overview.thin || []).length)}</strong>
              <small>{mappedLine}</small>
            </div>
          </div>
        </div>
      </div>
      <div className="ov-grid-viz">
        <div className="ov-panel">
          <h3>Coverage by area</h3>
          <small>{deptName ? 'How much Orbit knows in each area, across the whole company' : 'How much Orbit knows in each area'}</small>
          {overview.areas.length > 0 && <CoverageRadar areas={overview.areas} />}
          {overview.areas.length === 0 && <p className="facts-empty">Nothing read yet. Give Orbit a website or a document.</p>}
        </div>
        <div className="ov-panel">
          <h3>What Orbit knows</h3>
          <small>{deptName ? `Every card about ${deptName}, by where it stands` : 'Every card in the areas, by where it stands'}</small>
          <FactsDonut
            known={cards.known}
            confirm={cards.confirm}
            open={cards.open}
            onAnswer={() => setTab('journey')}
          />
        </div>
      </div>
      <div className="ov-stat two">
        {deptName && <div className="s"><b><CountUp value={overview.counts.onboarded} /></b><small>onboarded</small></div>}
        {!deptName && <div className="s"><b><CountUp value={overview.counts.departments} /></b><small>departments</small></div>}
        {peopleLinked && (
          <button type="button" className="s" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={openPeople}>
            <b><CountUp value={overview.counts.people} /></b><small>{deptName ? `people in ${deptName}` : 'people on file'} · see who</small>
          </button>
        )}
        {!peopleLinked && <div className="s"><b><CountUp value={overview.counts.people} /></b><small>people on file</small></div>}
      </div>
    </>
  );

  /* The partner's Journey is the client intake now: People and Tools from
     setup, the partner's own sections, and the sessions sent to one person.
     It replaces the phase list and its depth strip. */
  let editIntake;
  if (canPromote && frameworkPath) editIntake = toFramework;
  const journeyPane = <IntakeJourneyPanel clientId={clientId} onEditIntake={editIntake} onPeople={() => setTab('people')} />;
  /* ── Opportunities ────────────────────────────────────────────────── */
  const oppCard = (item: OrbitOpportunityItem): React.ReactNode => {
    let stateLine = item.state;
    if (item.value) stateLine = `${item.state} · ${item.value}`;
    let toneCls = 'val';
    if (item.tone === 'rdy') toneCls = 'rdy';
    if (item.tone === 'off') toneCls = '';
    const isDeclining = declining !== null && declining.id === item.id;
    const canDecideThis = Boolean(canDecide && item.request_id && (item.review_state === 'awaiting_client' || (item.kind === 'request' && (item.request_status === 'pending' || item.request_status === 'dept_approved'))));
    let decideWord = 'Confirm';
    if (item.kind === 'request') decideWord = 'Approve scoping';
    return (
      <div key={`${item.kind}:${item.id}`} className={clsx('opp', item.kind === 'request' && 'person')}>
        <div className="lv"><em>{item.level} · for {item.for}</em><b className={toneCls}>{stateLine}</b></div>
        <strong>{item.title}</strong>
        {item.summary && <p>{item.summary}</p>}
        <span className="type">{item.type}</span>
        {(item.stage || item.decided_by) && (
          <div className="meta">
            {item.stage && <span>{item.stage}</span>}
            {item.decided_by && <span>decided by {item.decided_by}</span>}
          </div>
        )}
        {isDeclining && (
          <div className="decline">
            <textarea rows={2} value={declining.note} onChange={(e) => setDeclining({ id: item.id, note: e.target.value })} placeholder="Why not, in a line. The person who raised it reads this." />
            <div className="acts" style={{ marginTop: 0 }}>
              <button type="button" className="pri" disabled={oppBusy || !declining.note.trim()} onClick={() => decide(item, 'declined', declining.note.trim())}>Decline with note</button>
              <button type="button" onClick={() => setDeclining(null)}>Keep it</button>
            </div>
          </div>
        )}
        {!isDeclining && (
          <div className="acts">
            {canDecideThis && <button type="button" className="pri" disabled={oppBusy} onClick={() => decide(item, 'approved')}>{decideWord}</button>}
            {canDecideThis && <button type="button" disabled={oppBusy} onClick={() => setDeclining({ id: item.id, note: '' })}>Decline</button>}
            {isPartner && item.review_state === 'ready_for_client' && <button type="button" className="pri" disabled={oppBusy} onClick={() => sendToClient(item)}>Send to the owner</button>}
            {!isPartner && <button type="button" className={clsx(!canDecideThis && 'pri')} onClick={() => navigate(item.link)}>Open</button>}
          </div>
        )}
      </div>
    );
  };
  const lockCard = (() => {
    const o = overview.opportunities;
    const unlock = o.unlock_ask;
    if (!o.locked || !unlock) return null;
    let word = `${o.open_count} answers away.`;
    if (o.open_count === 1) word = 'One answer away.';
    return (
      <div className="lock">
        <p className="kick">Opportunity radar</p>
        <strong>{word}</strong>
        <p>When <b>{responsibleLine(unlock, me, people)}</b> answers &quot;{unlock.question}&quot;, Orbit can price the first opportunity honestly: value, effort and risk.</p>
        {canAnswer(unlock) && <button type="button" onClick={() => setDrawer({ kind: 'ask', ask: unlock, mode: 'answer' })}>Answer it now</button>}
      </div>
    );
  })();
  const OPP_FILTERS: { id: OppFilter; label: string }[] = [
    { id: 'all', label: 'All' }, { id: 'company', label: 'Company' }, { id: 'person', label: 'From people' },
  ];
  const oppsPane = (() => {
    const shown = oppsFor(oppFilter);
    /* This held `lockCard && shown.length === 0 && (…)`, which is the BOOLEAN
       the last operand evaluates to, not the card. React draws nothing for a
       boolean, and the empty state below stood down for it, so a company with
       an unanswered problem and no opportunities yet got a blank page under
       the filters. The card itself is what belongs here. */
    let lock: React.ReactNode = null;
    if (lockCard && shown.length === 0 && (oppFilter === 'all' || oppFilter === 'company')) lock = lockCard;
    const scan = canDiscover && <button type="button" disabled={oppBusy} onClick={discover}>Find opportunities in the file</button>;
    let empty: React.ReactNode = (
      <div className="empty">
        <strong>No opportunities yet.</strong>
        They form as the maps fill, and Orbit prices one once the problem is confirmed and the answers are in.
        {canDiscover && <> If a document already names the work, let Orbit read it.</>}
        {scan}
      </div>
    );
    if (oppFilter === 'person') {
      empty = (
        <div className="empty">
          <strong>Nothing from people yet.</strong>
          What employees tell Orbit becomes an opportunity once the owner approves scoping it.
        </div>
      );
    }
    return (
      <>
        <div className="opp-filters" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          {OPP_FILTERS.map((f) => <button key={f.id} type="button" className={clsx(oppFilter === f.id && 'on')} onClick={() => setOppFilter(f.id)}>{f.label}</button>)}
          {canDiscover && <button type="button" style={{ marginLeft: 'auto' }} disabled={oppBusy} onClick={discover}>Find opportunities in the file</button>}
        </div>
        {shown.length > 0 && <div className="opps">{shown.map(oppCard)}</div>}
        {lock}
        {shown.length === 0 && !lock && empty}
      </>
    );
  })();

  /* ── one dossier ─────────────────────────────────────────────────── */
  const factRow = (f: OrbitRoomFact, i: number): React.ReactNode => {
    const sel = selFact === i;
    let openWord = 'Correct it';
    if (f.state === 'confirm') openWord = 'Not quite';
    let rsCls = 'rs ok';
    let source = f.source;
    if (f.state === 'confirm') { rsCls = 'rs warn'; source = `${f.source} · to confirm`; }
    if (f.confidence < 1) source = `${source} · ${Math.round(f.confidence * 100)}%`;
    let toggleTo = i;
    if (sel) toggleTo = -1;
    const editing = Boolean(inline && inline.id === f.field_key && inline.mode === 'correct');
    let standing = 'Confirmed by a person. Orbit will not ask it again.';
    if (f.state === 'confirm') standing = 'Read by Orbit, waiting for a person to agree. It is not asked as a question while it stands.';
    let by = '';
    if (f.by) by = `Confirmed by ${f.by}`;
    if (by && whenLabel(f.when)) by = `${by}, ${whenLabel(f.when)}`;
    return (
      <div key={f.field_key} id={`fact-row-${i}`} className={clsx('row-c', sel && 'sel open')}>
        <button type="button" onClick={() => setSelFact(toggleTo)}>
          <span className="rk">{keyOf(f.label)}</span>
          <span className="rq">{f.label}<small>{f.value_display || 'nothing recorded'}</small></span>
          <span className={rsCls}>{source}</span>
        </button>
        <div className="row-x">
          <div className="xline"><b>Answer</b><span>{f.value_display || 'nothing recorded'}</span></div>
          <div className="xline"><b>Source</b><span>{f.source}.{f.confidence < 1 ? ` Orbit is ${Math.round(f.confidence * 100)}% sure.` : ''}</span></div>
          <div className="xline"><b>Standing</b><span>{standing}</span></div>
          {by && <div className="xline"><b>By</b><span>{by}.</span></div>}
          <div className="acts">
            {f.state === 'confirm' && canConfirm && <button type="button" className="pri" onClick={() => confirmFact(f)}>That&apos;s right</button>}
            {canConfirm && <button type="button" className={clsx(editing && 'on')} onClick={() => startInline(f.field_key, 'correct', f.value_display)}>{openWord}</button>}
          </div>
          {editing && (
            <div className="row-in">
              <label>
                <b>New answer</b>
                <input
                  autoFocus
                  value={String(inline?.value ?? '')}
                  onChange={(e) => patchInline({ value: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveInlineCorrection(f); }}
                  placeholder={f.value_display || 'What it should say'}
                />
              </label>
              <div className="in-acts">
                <button type="button" className="pri" disabled={inlineBusy} onClick={() => saveInlineCorrection(f)}>Save</button>
                <button type="button" disabled={inlineBusy} onClick={() => setInline(null)}>Cancel</button>
                <small>Saved with you as the source. Orbit stops asking it.</small>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const askRow = (ask: OrbitAsk): React.ReactNode => {
    const isOpen = openAsk === ask.id;
    const who = responsibleLine(ask, me, people);
    // The word on the row is the word on the drawer's button: a partner always
    // hands a question to the owner, anyone else hands it to the person who knows.
    let askWord = 'Hand to someone';
    if (isPartner) askWord = 'Ask the owner';
    if (!isPartner && ask.responsible_email && ask.responsible_email !== me) {
      let shortWho = who;
      if (who.length > 14) shortWho = who.split(' ')[0];
      askWord = `Ask ${shortWho}`;
    }
    let kicker = originLine(ask, people);
    if (ask.origin === 'orbit_industry') kicker = 'Orbit, from your industry';
    if (ask.origin === 'orbit_tool') kicker = 'Orbit, routed by tool use';
    if (purpose === 'business' && ask.purpose === 'department' && ask.scope) kicker = `${kicker} · ${scopeName(ask.scope)}`;
    if (ask.onboarding) kicker = `${kicker} · in the deck`;
    const handed = handedLine(ask, people);
    if (handed) kicker = `${kicker} · ${handed}`;
    const chip = statusChip(ask, people);
    const answering = Boolean(inline && inline.id === ask.id && inline.mode === 'answer');
    const handing = Boolean(inline && inline.id === ask.id && inline.mode === 'hand');
    let answerNote = 'Saved with you as the source. Orbit stops asking it.';
    if (isPartner) answerNote = 'Saved as your answer. The owner sees it as to confirm.';
    let toggleTo = ask.id;
    if (isOpen) toggleTo = '';
    return (
      <div key={ask.id} className={clsx('row-c', isOpen && 'open')}>
        <button type="button" onClick={() => setOpenAsk(toggleTo)}>
          <span className="rk">{askKeyOf(ask)}</span>
          <span className="rq">{ask.question}</span>
          <span className="rs">→ {who}</span>
        </button>
        <div className="row-x">
          {ask.why && <div className="xline"><b>Why</b><span>{ask.why}</span></div>}
          <div className="xline"><b>Collects</b><span>{FORMAT[ask.kind] || 'text'}.</span></div>
          <div className="xline"><b>From</b><span>{kicker}.</span></div>
          <div className="xline"><b>State</b><span>{chip.label}.</span></div>
          <div className="acts">
            {canAnswer(ask) && <button type="button" className={clsx(answering && 'on')} onClick={() => startInline(ask.id, 'answer', firstValue(ask))}>Answer</button>}
            {canDelegate && <button type="button" className={clsx('pri', handing && 'on')} onClick={() => startInline(ask.id, 'hand', null, suggestedFor(ask))}>{askWord}</button>}
            {canContext && <button type="button" onClick={() => setDrawer({ kind: 'context' })}>From a doc</button>}
            {canPromote && ask.origin === 'orbit_industry' && !ask.field_key && <button type="button" onClick={() => promote(ask)}>Make it standard</button>}
            <button type="button" onClick={() => setDrawer({ kind: 'ask', ask, mode: 'detail' })}>Open</button>
          </div>
          {answering && (
            <div className="row-in">
              {inlineAnswerControl(ask)}
              <div className="in-acts">
                <button type="button" className="pri" disabled={inlineBusy} onClick={() => saveInlineAnswer(ask)}>Save the answer</button>
                <button type="button" disabled={inlineBusy} onClick={() => setInline(null)}>Cancel</button>
                <small>{answerNote}</small>
              </div>
            </div>
          )}
          {handing && (
            <div className="row-in">
              <label>
                <b>To</b>
                <select value={inline?.to || ''} onChange={(e) => patchInline({ to: e.target.value })}>
                  <option value="">Pick a person</option>
                  {targetsFor(ask).map((p) => <option key={p.email} value={p.email}>{p.name || p.email}{p.role ? ` · ${roleWord(p.role)}` : ''}</option>)}
                </select>
              </label>
              <label>
                <b>Note</b>
                <input value={inline?.note || ''} onChange={(e) => patchInline({ note: e.target.value })} placeholder="Optional. Why it is theirs to answer." />
              </label>
              <div className="in-acts">
                <button type="button" className="pri" disabled={inlineBusy} onClick={() => saveInlineHand(ask)}>{askWord}</button>
                <button type="button" disabled={inlineBusy} onClick={() => setInline(null)}>Cancel</button>
                <small>They answer it in their own Room. Nothing is emailed.</small>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const detail = (() => {
    if (!active) return null;
    if (loading && !view) return <Loading label={`Loading the ${WORDS.dossier.toLowerCase()}`} />;
    if (error || !view) return <ErrorNote message={error || `Could not load this ${WORDS.dossier.toLowerCase()}.`} />;
    let summary: React.ReactNode = <>{active.verdict}. {active.known} filled, {active.confirm} to confirm, {active.ask} to collect.</>;
    if (active.kind === 'company' && overview.sentence) summary = overview.sentence;
    if (active.kind === 'department') {
      let head = 'No head named yet.';
      if (active.head) head = `${active.head} runs it.`;
      summary = <>{head} {plural(active.members || 0, 'person', 'people')} on the roster, {active.onboarded || 0} onboarded. {active.known} filled, {active.ask} to collect.</>;
    }
    if (active.kind === 'people') summary = <>{plural(active.members || 0, 'person', 'people')} on file across {plural(overview.counts.departments, 'department', 'departments')}, {active.heads || 0} with a head. Everyone added gets Orbit access: <em>{WORDS.cards} route to whoever can actually answer</em>.</>;
    if (active.kind === 'tools') summary = <>{active.known + active.confirm} tools on file. <em>Permissions come later, per opportunity, through the connectors</em>, never up front.</>;
    const dossierOpps = oppsFor('all', active);
    let oppCount = dossierOpps.length;
    if (oppCount === 0 && active.kind === 'company' && overview.opportunities.locked) oppCount = overview.opportunities.open_count;
    const detailTabs: { id: Fill; label: string; count: number }[] = [
      { id: 'collected', label: WORDS.collected, count: facts.length },
      { id: 'tocollect', label: WORDS.toCollect, count: asks.length },
      { id: 'opps', label: WORDS.opportunities, count: oppCount },
    ];
    if (active.kind === 'people' && canPeople) {
      detailTabs.push({ id: 'team', label: 'Team', count: active.members || 0 });
      detailTabs.push({ id: 'departments', label: 'Departments', count: overview.counts.departments });
    }
    if (active.kind === 'tools' && viewer !== 'employee') detailTabs.push({ id: 'connections', label: 'Connections', count: active.known + active.confirm });
    const showAdd = fill === 'tocollect' && canAdd && viewer !== 'employee';
    const showPeopleActs = active.kind === 'people' && canPeople && (fill === 'team' || fill === 'departments');

    const body = (() => {
      if (fill === 'collected') {
        if (facts.length === 0) {
          let reason = `Answer a ${WORDS.card} or drop a document.`;
          if (view.unavailable) reason = 'The file could not be read right now. Try again shortly.';
          return <div className="empty"><strong>Nothing collected yet.</strong>{reason}</div>;
        }
        const hand = facts.slice(0, 7);
        const mid = (hand.length - 1) / 2;
        let spread = 96;
        let tilt = 9;
        if (hand.length > 5) { spread = 86; tilt = 7; }
        return (
          <>
            <div className="fan-wrap">
              <div className="fan">
                {hand.map((f, i) => {
                  const r = (i - mid) * tilt;
                  const x = (i - mid) * spread;
                  const style = { '--r': `${r}deg`, '--x': `${x}px`, transform: `translateX(${x}px) rotate(${r}deg)`, zIndex: 10 + i } as React.CSSProperties;
                  return (
                    <button
                      key={f.field_key}
                      type="button"
                      className={clsx('fcard', selFact === i && 'sel')}
                      style={style}
                      onClick={() => { setSelFact(i); const row = document.getElementById(`fact-row-${i}`); if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                    >
                      <div className="k">{keyOf(f.label)}</div><div className="v">{f.value_display || f.label}</div><div className="s">{f.source}</div>
                    </button>
                  );
                })}
              </div>
              <div className="fan-hint">Your hand · {plural(facts.length, WORDS.card, WORDS.cards)} collected. Tap one.</div>
            </div>
            <div className="rows">{facts.map(factRow)}</div>
          </>
        );
      }
      if (fill === 'tocollect') {
        if (asks.length === 0) return <div className="empty"><strong>Nothing to collect.</strong>This {WORDS.dossier.toLowerCase()} is sufficient for now.</div>;
        return <div className="rows">{asks.map(askRow)}</div>;
      }
      if (fill === 'team' || fill === 'departments') {
        return (
          <div className="embedded">
            <p>The roster and the departments, managed here until the team screen Musawir is drawing lands. Inviting sends an email and takes a seat; adding context does not.</p>
            <CompanyTabPanel tab={fill} clientId={clientId} clientName={storedClientName} role={localStorage.getItem('user_role') || undefined} />
          </div>
        );
      }
      if (fill === 'connections') {
        return (
          <div className="embedded">
            <p>Tools on file that are not connected get two {WORDS.cards} in {WORDS.toCollect}: what the tool is for, and what Orbit may do there. Saying &quot;connect&quot; raises a request to the owner. This is where the owner connects them.</p>
            <ConnectorsCatalog onConnected={reload} />
          </div>
        );
      }
      if (dossierOpps.length > 0) return <div className="opps">{dossierOpps.map(oppCard)}</div>;
      if (active.kind === 'company' && lockCard) return lockCard;
      return <div className="empty"><strong>No opportunities on this {WORDS.dossier.toLowerCase()} yet.</strong>They form as {WORDS.cards} fill: sufficiency first, suggestions second.</div>;
    })();

    let pct: React.ReactNode = <span className="pct"><b>{active.pct}%</b></span>;
    if (active.head) pct = <span className="pct"><b>{active.pct}%</b> · {active.head}</span>;
    return (
      <>
        <div className="det-head">
          {/* A head has nowhere to go back TO: their department is the screen,
              not one dossier among the company's. */}
          {!isDept && <button type="button" className="back" onClick={back}>← Orbit</button>}
          {isDept && ownPart && <button type="button" className="back" onClick={() => setDeptPart(true)}>← Your part</button>}
          <h1>{active.name}</h1>
          {pct}
        </div>
        <div className="det-sum panel tint">
          <p className="nl">{summary}</p>
          <div className="srcline">{srcline(dossierSources)}</div>
        </div>
        {isDept && view?.company_summary && <CompanyCount summary={view.company_summary} />}
        <div className="dtab-row">
          <nav className="dtabs">
            {detailTabs.map((t) => (
              <button key={t.id} type="button" className={clsx('dtab', fill === t.id && 'on')} onClick={() => { setFill(t.id); setOpenAsk(''); }}>{t.label}<b>{t.count}</b></button>
            ))}
          </nav>
          {showAdd && <button type="button" className="addcard" onClick={() => setDrawer({ kind: 'addq' })}>+ {WORDS.addCard}</button>}
          {showPeopleActs && (
            <div className="aux-acts" style={{ marginLeft: 'auto' }}>
              {canDepartments && <button type="button" onClick={() => setDrawer({ kind: 'adddept' })}>Add a department</button>}
              <button type="button" onClick={() => setDrawer({ kind: 'addperson' })}>Add person</button>
              <button type="button" onClick={() => setDrawer({ kind: 'import' })}>Import CSV</button>
            </div>
          )}
        </div>
        {body}
      </>
    );
  })();

  /* ── the open phase ───────────────────────────────────────────────── */
  const allFacts: OrbitRoomFact[] = view ? view.buckets.flatMap((b) => b.facts) : [];
  // The opening questions first, then the rest. The server stamps the order;
  // the buckets are flattened here, so the sort has to happen here too or the
  // bucket order would scatter the six.
  const allAsks: OrbitAsk[] = view
    ? view.buckets.flatMap((b) => b.asks).slice().sort((a, b) => (a.opening_rank ?? 999) - (b.opening_rank ?? 999))
    : [];
  const doors = dossiers.filter((d) => d.kind === 'department' || d.kind === 'proposed_department');

  // Beside the cards, the same panel the Journey carries. Handing Orbit a
  // document is an alternative to answering, so it belongs next to the deck
  // rather than behind a button that covers it.
  let fullMapAside: React.ReactNode = null;
  if (canContext || active) {
    fullMapAside = (
      <>
        {canContext && <JourneyContext clientId={clientId} onAdded={reload} />}
        {active && (
          <div className="aux-acts">
            <button type="button" onClick={() => setPhase(0)}>See everything Orbit holds</button>
          </div>
        )}
      </>
    );
  }

  /* A step opens the intake it names, not whatever sits at its number, so a
     partner can put the team before the departments and the right screen
     still opens. */
  const screen = screenOfPhase(journey, phase);
  const openFrameStep = () => { setPhase(frameN); };
  const phaseScreen = (() => {
    if (phase === 0) return null;
    // A map opened from inside a phase is answered as a deck; the full map with
    // every source and correction stays a click away.
    if (active) {
      return (
        <MapFill
          clientId={clientId}
          title={active.name}
          purpose={<>Orbit drafted {active.name} from what it read. These cards make it <b>true</b>: the work as it really runs, the numbers that become baselines, and where it leans on people.</>}
          backLabel={screen === 'departments' ? 'Departments' : 'Company'}
          onBack={() => setOpenKey('')}
          held={facts}
          asks={asks}
          isPartner={isPartner}
          onChanged={reload}
          aside={fullMapAside}
          done={{
            title: `${active.name} is true.`,
            built: [`${active.known} facts on file, each with its source`, 'Everything Orbit could not answer has been asked', 'The next department opens'],
            action: 'Back to the departments',
            onAction: () => setOpenKey(''),
          }}
        />
      );
    }
    if (screen === 'frame') {
      const frameStep = shownPhases(journey).find((p) => p.n === phase);
      return (
        <MapFill
          clientId={clientId}
          title={frameStep ? frameStep.title : 'Business intake'}
          purpose={<>Intake gave Orbit the outside. These cards do only what the outside can never give: <b>correct what is wrong, react to what Orbit suspects, open the doors.</b> Nothing you already answered is asked again.</>}
          backLabel="Company"
          onBack={toCompany}
          held={allFacts}
          asks={allAsks}
          isPartner={isPartner}
          onChanged={reload}
          aside={fullMapAside}
          onComplete={closeFrame}
          done={{
            title: `${frameStep ? frameStep.title : 'Business intake'} is done.`,
            built: ['The map, corrected, including what no website could see', 'What Orbit suspects, put to a person', 'The doors: heads, documents and systems named'],
            action: 'Back to your company, the next phase is ready',
            onAction: toCompany,
          }}
        />
      );
    }
    if (screen === 'departments') {
      return (
        <PhaseDepartments
          clientId={clientId}
          departments={doors}
          people={people}
          rooms={view ? view.departments : []}
          canPeople={canPeople}
          canInvite={canPeople}
          canDepartments={canDepartments}
          onBack={toCompany}
          onOpen={(key) => open(key)}
          onAddDepartment={() => setDrawer({ kind: 'adddept' })}
          onDecide={decideProposal}
          onChanged={reload}
        />
      );
    }
    if (screen === 'team') {
      /* The Person intake: each person's own questions. The team roster is
         not a journey step; it stays in People. */
      const personStep = shownPhases(journey).find((p) => p.n === phase);
      return (
        <PhasePersonIntake
          clientId={clientId}
          title={personStep ? personStep.title : 'Person intake'}
          onBack={toCompany}
          onChanged={reload}
        />
      );
    }
    if (screen === 'tools') {
      const stack = view ? view.buckets.filter((b) => b.key === 'stack') : [];
      return (
        <PhaseTools
          clientId={clientId}
          facts={stack.flatMap((b) => b.facts)}
          asks={stack.flatMap((b) => b.asks)}
          onBack={toCompany}
          onChanged={reload}
          onNameTools={openFrameStep}
          onOpenFrame={openFrameStep}
        />
      );
    }
    if (screen === 'report') {
      return (
        <ClarityReport
          overview={overview}
          isPartner={isPartner}
          onBack={toCompany}
          onOpenOpportunities={() => { setPhase(0); setTab('opps'); }}
        />
      );
    }
    if (screen.startsWith('intake:')) {
      /* An intake the partner defined: its own cards, answered as a deck like
         every other map. */
      const step = shownPhases(journey).find((p) => p.n === phase);
      const title = step ? step.title : 'Your intake';
      const intakeAsks = allAsks.filter((a) => a.scope === screen);
      return (
        <MapFill
          clientId={clientId}
          title={title}
          purpose={<>{step && step.purpose ? step.purpose : `The questions in ${title}, answered once for the company.`}</>}
          backLabel="Company"
          onBack={toCompany}
          held={[]}
          asks={intakeAsks}
          isPartner={isPartner}
          onChanged={reload}
          aside={fullMapAside}
          done={{
            title: `${title} is answered.`,
            built: [`Every question in ${title} answered or handed to the right person`],
            action: 'Back to your company',
            onAction: toCompany,
          }}
        />
      );
    }
    if (screen === 'run') {
      return <RunView overview={overview} onBack={toCompany} onOpenProjects={() => navigate(isSolutionPartner ? '/partner/projects' : '/projects')} />;
    }
    // A step with no intake of its own (a workshop, a sign-off) still sits in
    // the order; it shows what it is for and is closed from the journey.
    const step = shownPhases(journey).find((p) => p.n === phase);
    if (!step) return null;
    return (
      <div className="step-plain">
        <button type="button" className="back" onClick={toCompany}>Back to your company</button>
        <p className="kick">Phase {step.n}</p>
        <h2>{step.title}</h2>
        {step.purpose && <p>{step.purpose}</p>}
        {step.builds.length > 0 && (
          <ul>{step.builds.map((b) => <li key={b}>{b}</li>)}</ul>
        )}
        <p className="note">This step has no questions of its own. Mark it done from the journey once it has happened.</p>
      </div>
    );
  })();

  let main: React.ReactNode = overviewPane;
  const myPartPane = (
    <MyPart
      clientId={clientId}
      backLabel={roomTitle}
      // A head sees only their part: the department's detail view is parked for them.
      onBack={isDept ? undefined : () => setTab('overview')}
      onOpenDepartment={(slug) => { if (isDept) return; setTab('overview'); open(slug); }}
      onAddDepartment={() => setDrawer({ kind: 'adddept' })}
      onInvite={() => setDrawer({ kind: 'addperson' })}
      onOpenTools={() => window.dispatchEvent(new CustomEvent('settings:open', { detail: { section: 'connectors' } }))}
      onOpenWork={(link) => navigate(link)}
    />
  );
  if (tab === 'journey') main = ownPart ? myPartPane : journeyPane;
  if (tab === 'opps') main = oppsPane;
  if (tab === 'people') main = <PeopleArea key={`${askedTab}:${askedPerson}:${askedSession}`} clientId={clientId} initialTab={peopleTab} initialEmail={askedPerson} initialSession={askedSession} />;
  /*
   * Journey and Opportunities are screens, not tabs.
   *
   * They were drawn under the Room's own head and tab row, so the reader was
   * inside two places at once: the Room saying it is the company dossier by
   * dossier, and the five phases underneath disagreeing. Each takes the whole
   * surface now and carries the one way back, which is the shape a dossier and
   * a phase already had. The Room is what you come back TO.
   */
  const onTabScreen = tab !== 'overview';
  const tabHead = TABS.find((t) => t.id === tab);
  const chromeOff = Boolean(active) || phase > 0 || onTabScreen;
  const toRoom = () => {
    setTab('overview');
    if (hideOverview) {
      window.setTimeout(() => document.getElementById('orbit-room')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  let tabScreen: React.ReactNode = null;
  if (onTabScreen && tabHead && tab === 'journey' && ownPart) {
    tabScreen = main;
  } else if (onTabScreen && tabHead) {
    tabScreen = (
      <>
        <div className="det-head">
          <button type="button" className="back" onClick={toRoom}>&larr; {roomTitle}</button>
          {/* People carries its own tabs (People, New session, Sessions), so a title above them only repeats the first. */}
          {tab !== 'people' && <h1>{tabHead.label}</h1>}
        </div>
        {main}
      </>
    );
  }

  let shell: React.ReactNode = (
    <Panel>
      {/* The Room names itself only while you are in it. On a screen of its
          own the head would title the page something it is not. */}
      {!onTabScreen && <PanelHead kicker="Orbit" title={roomTitle} desc={roomDesc} action={headActions} />}
      <div className={clsx('oroom px-5 pb-5 md:px-6', !onTabScreen && 'pt-2', onTabScreen && 'pt-5')}>
        {!chromeOff && tabs}
        {!chromeOff && !hideOverview && main}
        {phase === 0 && !active && tabScreen}
        {phase > 0 && phaseScreen}
        {phase === 0 && active && isDept && ownPart && deptPart && myPartPane}
        {phase === 0 && active && !(isDept && ownPart && deptPart) && detail}
      </div>
    </Panel>
  );
  if (layout === 'page') {
    shell = (
      <div className="oroom">
        {!chromeOff && (
          <>
            <p className="kick">{eyebrow}</p>
            <div className="room-head">
              <h1>{pageName}</h1>
              {headActions}
            </div>
            {tabs}
            {main}
          </>
        )}
        {phase === 0 && !active && tabScreen}
        {phase > 0 && phaseScreen}
        {phase === 0 && active && isDept && ownPart && deptPart && myPartPane}
        {phase === 0 && active && !(isDept && ownPart && deptPart) && detail}
      </div>
    );
  }

  return (
    <>
      {shell}

      {drawer && drawer.kind === 'ask' && (
        <AskDrawer
          ask={drawer.ask} mode={drawer.mode} me={me} people={people}
          canAnswer={canAnswer(drawer.ask)} canDelegate={canDelegate} canEdit={canEdit(drawer.ask)} isPartner={isPartner}
          onClose={() => setDrawer(null)} onChanged={reload}
        />
      )}
      {drawer && drawer.kind === 'addq' && view && (
        <AddQuestionDrawer purpose={purpose} bucket={active ? bucketOfDossier(active.kind, active.bucket) : 'company'} scope={view.scope} me={me} people={people} isPartner={isPartner} onClose={() => setDrawer(null)} onCreated={reload} />
      )}
      {drawer && drawer.kind === 'context' && (
        <AddContextDrawer clientId={clientId} onClose={() => setDrawer(null)} onDone={reload} />
      )}
      {drawer && drawer.kind === 'adddept' && (
        <AddDepartmentDrawer people={people} onClose={() => setDrawer(null)} onDone={reload} />
      )}
      {drawer && drawer.kind === 'addperson' && view && (
        <AddPersonDrawer clientId={clientId} departments={view.departments} onClose={() => setDrawer(null)} onDone={reload} />
      )}
      {drawer && drawer.kind === 'import' && (
        <ImportRosterDrawer clientId={clientId} onClose={() => setDrawer(null)} onDone={reload} />
      )}
    </>
  );
};

export default OrbitRoomPanel;
